import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from '@repo/ui'
import { Check, ChevronDown, LoaderCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { stageLabel } from './eventCopy'
import type { ReactNode } from 'react'
import type { ConversationExecutionState, ConversationState, ConversationTurnPresentation } from './state'

// The announcement describes the chat now, not the highest execution id ever created. A
// chat that finished one task and is waiting on a question for the next still read as
// `Complete`, which told the user the opposite of what was true.
//
// This came from Status.tsx, the strip below the composer, which this component
// replaces. The bar covers only work in flight, so the announcement is what carries the
// terminal and waiting states a screen reader still needs.
function boundedAnnouncement(state: ConversationState): string {
  if (state.questions.length > 0) return 'Waiting for your answer'
  const executions = orderedExecutions(state)
  // Shares selectLiveExecution and isWriting with the bar below rather than repeating
  // them. Two copies of "which execution is live" forty lines apart would let the live
  // region contradict the indicator directly above it.
  const live = selectLiveExecution(state)
  if (live?.summary !== undefined && live.summary.trim() !== '') return live.summary.slice(0, 160)
  if (live?.stage !== undefined && live.stage.trim() !== '') return stageLabel(live.stage).slice(0, 128)
  if (isWriting(state)) return 'Writing the response'
  if (state.exhausted.length > 0) return 'Stopped asking, some details are still open'
  // Snapshot roots are ordered newest first by creation time and origin id.
  const currentTask = state.tasks[0]
  if (currentTask?.intakeStatus === 'failed')
    return 'Could not start this task. Check your sources and send your request again.'
  if (currentTask?.intakeStatus === 'cancelled') return 'Task cancelled'
  const intake = intakeProgress(state)
  if (intake !== undefined) return intake.label
  // Nothing is in flight, so the most recent execution's terminal state is the current state.
  const currentExecution = executions[0]
  if (currentExecution?.status === 'succeeded') return 'Complete'
  if (currentExecution?.status === 'failed') return 'Work stopped. You can retry when ready.'
  if (currentExecution?.status === 'cancelled') return 'Work cancelled'
  if (currentExecution?.stage !== undefined && currentExecution.stage.trim() !== '') {
    return stageLabel(currentExecution.stage).slice(0, 128)
  }
  const stage = currentTask?.executionStage ?? currentTask?.questionStage ?? currentTask?.intakeStage
  return stageLabel(stage).slice(0, 128)
}

export type ConversationProgressStep = { key: string; label: string; state: 'done' | 'running' | 'pending' }

export type ConversationProgressView = {
  label: string
  startedAtTsMs?: number
  // Empty when the run cannot be placed on its graph, which renders as an indeterminate
  // bar with no trail and no "step N of M".
  steps: Array<ConversationProgressStep>
  index: number
}

/** Executions newest first, which is the order both the bar and the announcement read in. */
function orderedExecutions(state: ConversationState): Array<ConversationExecutionState> {
  return Object.entries(state.executionStates)
    .map(([executionId, execution]) => [Number(executionId), execution] as const)
    .sort(([left], [right]) => right - left)
    .map(([, execution]) => execution)
}

/**
 * The execution the chat is on now, which is the newest one that has not reached a
 * terminal state. Reading the highest execution id instead told a chat that had finished
 * one task and started another that it was still complete.
 */
export function selectLiveExecution(state: ConversationState): ConversationExecutionState | undefined {
  return orderedExecutions(state).find(
    (execution) => execution.status === 'queued' || execution.status === 'running' || execution.status === undefined
  )
}

/**
 * An attempt only means work in flight while its execution is not terminal: the reducer
 * clears attempts when one finishes, and a stale attempt must not outrank the result.
 */
function isWriting(state: ConversationState): boolean {
  return Object.values(state.attempts).some((attempt) => {
    const status = state.executionStates[attempt.executionId]?.status
    return status === undefined || status === 'queued' || status === 'running'
  })
}

/**
 * The trail the run announced, named. A key with no copy collapses the whole trail rather
 * than rendering an identifier beside its named siblings: eventCopy's rule is that
 * silence beats a machine token, and a blank row in a numbered list is worse than no
 * list. It also keeps the pressure where it belongs, on adding the copy.
 */
function namedSteps(steps: Array<string>, stage: string | undefined): Array<ConversationProgressStep> {
  const index = stage === undefined ? -1 : steps.indexOf(stage)
  if (steps.length === 0 || index < 0) return []
  const named: Array<ConversationProgressStep> = []
  for (const [position, key] of steps.entries()) {
    const label = stageLabel(key)
    if (label === '') return []
    named.push({ key, label, state: position < index ? 'done' : position === index ? 'running' : 'pending' })
  }
  return named
}

/**
 * What the indicator shows, or nothing when the chat is not working. An unanswered
 * question and a spent ask budget both render their own controls in the transcript, and
 * a bar above them would claim work is running when the chat is waiting on the reader.
 */
export function selectConversationProgress(state: ConversationState): ConversationProgressView | undefined {
  if (state.questions.length > 0 || state.exhausted.length > 0) return undefined
  const live = selectLiveExecution(state)
  const writing = isWriting(state)
  if (live === undefined && !writing) return intakeProgress(state)
  const stage = live?.stage
  const steps = namedSteps(live?.steps ?? [], stage)
  const staged = stage === undefined ? '' : stageLabel(stage)
  const label = live?.summary || (staged !== '' ? staged : writing ? 'Writing the response' : 'Working')
  const startedAtTsMs = liveExecutionStartedAt(state)
  return {
    label,
    steps,
    index: steps.findIndex((step) => step.state === 'running'),
    ...(startedAtTsMs === undefined ? {} : { startedAtTsMs })
  }
}

/** The arrival stamp of whichever execution the bar is describing. */
function liveExecutionStartedAt(state: ConversationState): number | undefined {
  const liveId = Object.entries(state.executionStates)
    .map(([executionId, execution]) => [Number(executionId), execution] as const)
    .sort(([left], [right]) => right - left)
    .find(
      ([, execution]) =>
        execution.status === 'queued' || execution.status === 'running' || execution.status === undefined
    )?.[0]
  return liveId === undefined ? undefined : state.executionStartedAt[liveId]
}

/**
 * Intake runs before any execution exists, so its frames carry no execution id and none of
 * the state above sees them. Without this the transcript is silent from the moment the
 * reader sends until the work is queued, which is routing plus brief extraction and is not
 * instant. It reads its stage off the task rather than off a frame, and is indeterminate:
 * intake is one workflow with no trail to place a reader on.
 *
 * Bounded by the task's own intake run. A refusal is the only thing that cards a status
 * now, so a card carrying this run's id means intake finished without producing an
 * execution, and the spinner must stop rather than hang on the last stage it saw.
 */
function intakeProgress(state: ConversationState): ConversationProgressView | undefined {
  const task = state.tasks[0]
  if (task === undefined || (task.executionId !== undefined && task.executionId !== null)) return undefined
  if (task.intakeStatus === 'failed' || task.intakeStatus === 'cancelled') return undefined
  // Once any execution exists the execution phase owns the indicator, and the caller has
  // already established that none of them is live. Reading the task alone is not enough:
  // a terminal status frame sets no snapshot refresh, so `tasks` still holds the task as
  // it was before its execution existed, with a null executionId and whatever question
  // stage it last carried. A local smoke caught this as a bar reading `Preparing a
  // question` beside a `Work stopped` card, contradicting the announcement next to it.
  const intakeIsLive = task.intakeStatus === 'queued' || task.intakeStatus === 'running'
  if (!intakeIsLive && Object.keys(state.executionStates).length > 0) return undefined
  // An intake that ends in a refusal never creates an execution, so the guard above
  // cannot stop it. A refusal is the only thing that cards a status now.
  if (state.changes.some((change) => change.kind === 'status' && change.runId === task.intakeRunId)) return undefined
  const label = stageLabel(task.questionStage ?? task.intakeStage)
  return label === '' ? undefined : { label, steps: [], index: -1 }
}

/** Elapsed as m:ss, which is the range a task actually runs for. */
export function elapsedLabel(sinceTsMs: number, nowTsMs: number): string {
  const seconds = Math.max(0, Math.floor((nowTsMs - sinceTsMs) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * A clock that only ticks while something is being timed, so a settled chat holds no
 * interval. The suite fails on leaked timers, and an always-on tick is one.
 *
 * Exported for the canvas refine composer, which times work of its own. One clock so the
 * two surfaces cannot disagree about what "1:05" means, and so the no-leaked-timer rule is
 * kept in one place rather than re-derived.
 */
export function useElapsed(startedAtTsMs: number | undefined): string | undefined {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (startedAtTsMs === undefined) return
    // The interval is the only writer. Seeding it here instead would be a synchronous
    // setState in an effect, and the reading is at most one tick stale either way.
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [startedAtTsMs])
  return startedAtTsMs === undefined ? undefined : elapsedLabel(startedAtTsMs, now)
}

/**
 * Live progress, in the transcript rather than in a strip below the composer. The strip
 * reported a stage that only ever arrived inside a snapshot fetched at connect, so a
 * deck build showed no movement for the whole of it, and it sat where the reader was not
 * looking. The sr-only region carries the same announcement the strip made, including
 * the terminal states this has no bar for.
 */
export function ConversationProgress({ state }: { state: ConversationState }) {
  const progress = selectConversationProgress(state)
  const elapsed = useElapsed(progress?.startedAtTsMs)
  const announcement = boundedAnnouncement(state)
  const determinate = progress !== undefined && progress.steps.length > 0
  return (
    <>
      {progress !== undefined && (
        <article className="flex flex-col gap-2" aria-label="Snowvault is working" data-testid="conversation-progress">
          <span className="flex items-center gap-2 text-sm">
            <LoaderCircle
              className="size-4 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate font-medium">{progress.label}</span>
            {determinate && (
              <span
                className="shrink-0 text-xs text-muted-foreground tabular-nums"
                data-testid="conversation-progress-position"
              >
                {`Step ${progress.index + 1} of ${progress.steps.length}`}
              </span>
            )}
            {elapsed !== undefined && (
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{elapsed}</span>
            )}
          </span>
          <span
            className="h-1 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="Work progress"
            {...(determinate
              ? { 'aria-valuenow': progress.index + 1, 'aria-valuemin': 1, 'aria-valuemax': progress.steps.length }
              : {})}
          >
            <span
              className={
                determinate
                  ? 'block h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none'
                  : 'block h-full w-1/3 rounded-full bg-primary motion-safe:animate-pulse'
              }
              {...(determinate ? { style: { width: `${((progress.index + 1) / progress.steps.length) * 100}%` } } : {})}
            />
          </span>
          {determinate && (
            <ol className="flex flex-col gap-1" data-testid="conversation-progress-trail">
              {progress.steps.map((step) => (
                <li
                  key={step.key}
                  data-step-state={step.state}
                  className={step.state === 'pending' ? 'text-xs text-muted-foreground' : 'text-xs text-foreground'}
                >
                  {step.label}
                </li>
              ))}
            </ol>
          )}
        </article>
      )}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </>
  )
}

function completedSummary(turn: ConversationTurnPresentation): string {
  if (turn.executionSteps.length > 0) return `Worked through ${turn.meaningfulActivityCount} steps`
  return `Completed ${turn.meaningfulActivityCount} updates`
}

function executionDetails({
  turn,
  children,
  onViewActivity
}: {
  turn: ConversationTurnPresentation
  children?: ReactNode
  onViewActivity?: () => void
}) {
  const named = turn.executionSteps.map((step) => ({ key: step, label: stageLabel(step) })).filter((step) => step.label)
  return (
    <div className="flex flex-col gap-1 border-l border-border/70 pl-3.5" data-testid="conversation-activity-group">
      {named.map((step) => (
        <div
          key={step.key}
          className="flex min-h-6 items-center gap-2 text-[0.8125rem] leading-5 text-muted-foreground"
        >
          <Check className="size-3.5 shrink-0" aria-hidden="true" />
          <span>{step.label}</span>
        </div>
      ))}
      {children}
      {onViewActivity !== undefined && (
        <Button type="button" variant="ghost" size="sm" className="h-7 w-fit px-2 text-xs" onClick={onViewActivity}>
          View activity details
        </Button>
      )}
    </div>
  )
}

/**
 * One execution surface per request. Live work is a single line. Completed work keeps
 * one to three meaningful entries visible and puts longer histories behind a disclosure.
 * The child slot carries durable change or recovery rows already owned by the turn.
 */
export function ConversationExecutionDisclosure({
  turn,
  children,
  onViewActivity
}: {
  turn: ConversationTurnPresentation
  children?: ReactNode
  onViewActivity?: () => void
}) {
  const [open, setOpen] = useState(false)
  const elapsed = useElapsed(
    turn.phase === 'submitted' || turn.phase === 'working' || turn.phase === 'writing' ? turn.startedAtTsMs : undefined
  )
  const live = turn.phase === 'submitted' || turn.phase === 'working' || turn.phase === 'writing'
  if (live) {
    const staged = stageLabel(turn.activeStage)
    const label = turn.activeSummary || staged || (turn.phase === 'writing' ? 'Writing the response' : 'Working')
    return (
      <div
        className="flex min-h-6 items-center gap-2 text-sm text-muted-foreground"
        data-testid="conversation-execution-live"
      >
        <LoaderCircle className="size-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {elapsed !== undefined && <span className="shrink-0 text-xs tabular-nums">{elapsed}</span>}
      </div>
    )
  }

  if (turn.meaningfulActivityCount === 0 && children === undefined) return null
  const details = executionDetails({
    turn,
    ...(children === undefined ? {} : { children }),
    ...(onViewActivity === undefined ? {} : { onViewActivity })
  })
  const collapsed = turn.phase === 'complete' && turn.meaningfulActivityCount >= 4
  if (!collapsed) return details

  const summary = completedSummary(turn)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-1">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-h-8 w-fit max-w-full items-center gap-2 rounded-md px-2 text-left text-[0.8125rem] text-muted-foreground outline-none hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={summary}
        >
          <ChevronDown
            className={cn('size-3.5 shrink-0 transition-transform motion-reduce:transition-none', open && 'rotate-180')}
            aria-hidden="true"
          />
          <span className="truncate">{summary}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>{details}</CollapsibleContent>
    </Collapsible>
  )
}
